import React, { memo } from 'react';
import { tokens } from '@beeline.com/bp-design-system';
import { AvatarModel, buildMediaPicUri } from 'src/utils/UserUtils';
import defaultAvatarUrl from 'src/static/images/default-avatar.png';
import { logger } from 'src/utils/logger';
import Avatar, { AvatarProps } from './Avatar';
import { AvatarSize } from './Avatar.types';
import { imageResizerUpdatePersonModelProfilePhotoSignedUrl } from '../useImageResizer';
import { PersonModel } from '@utmost.co/enterprise-models';
import { avatarImageResizeSize } from './useGetAvatarImageResizerSize';
import { useIntlFormat } from 'utils/intl/useIntlFormat';

export interface UserAvatarProps extends AvatarProps {
  user?: DeepMaybe<AvatarModel>;
  cacheBust?: boolean;
  editable?: boolean;
  onClick?: (e: any) => void;
  size?: AvatarSize;
  alt?: string;
  hideStatus?: boolean;
  className?: string;
  dataTest?: string;
  statusTooltip?: boolean;
}

const getLetterAvatarInitials = (fullName?: string | null): string | null => {
  if (!fullName) return null;
  const [firstName, secondName] = fullName.trim().replace(/-/g, ' ').split(' ');

  const firstLetter = firstName?.[0] ?? '';
  const secondLetter = secondName?.[0] ?? '';

  return `${firstLetter.toLocaleUpperCase()}${secondLetter.toLocaleUpperCase()}`;
};
/* To get proper Avatar (Letter Avatar) you should provide:
  - fullName
  - personUid
  - profilePhoto: { uid }
  - status (optionally)
*/
export const UserAvatar: React.FC<React.PropsWithChildren<UserAvatarProps>> = ({
  user,
  dataTest,
  hideStatus,
  statusTooltip,
  style,
  className,
  ...avatarProps
}) => {
  const avatarImageResize = avatarImageResizeSize(avatarProps?.size ?? 'xs');
  const format = useIntlFormat();
  const imageResizer = imageResizerUpdatePersonModelProfilePhotoSignedUrl({
    model: user as DeepPartial<PersonModel>,
    width: avatarImageResize,
    height: avatarImageResize,
    fit: 'COVER'
  });
  const profilePhoto = imageResizer?.profilePhoto ?? user?.profilePhoto ?? undefined;
  const personUid = imageResizer?.personUid ?? (user?.personUid || user?.uid) ?? undefined;
  const src = buildMediaPicUri({ profilePhoto, personUid });
  const letterAvatar = getLetterAvatarInitials(user?.fullName || user?.personName?.fullName || user?.email);
  if ('hasProfilePhoto' in ((user as any) || {})) {
    logger.warn('hasProfilePhoto is prohibited, as it slows down requests containing this field');
  }
  const showStatus = !hideStatus && !!user?.status;
  return (
    <Avatar
      {...avatarProps}
      style={!showStatus ? style : undefined}
      className={!showStatus ? className : undefined}
      initials={letterAvatar}
      backgroundColorUid={personUid}
      statusColor={showStatus ? STATUS_MAPPER[(user?.status as keyof typeof STATUS_MAPPER) ?? ''] : undefined}
      slotProps={{
        Status: {
          style: showStatus ? style : undefined,
          className: showStatus ? className : undefined
        },
        Tooltip: {
          title:
            showStatus && !!statusTooltip ? format(`common.avatar.userStatus${user?.status ?? 'DISABLED'}`) : undefined
        }
      }}
      defaultSrc={defaultAvatarUrl}
      dataTest={dataTest}
      src={src}
    />
  );
};

const STATUS_MAPPER: Record<'DELETED' | 'DISABLED' | 'ENABLED', string> = {
  DELETED: tokens.colorAlert,
  DISABLED: tokens.colorOnSurface,
  ENABLED: tokens.colorPrimary
};

export default memo<UserAvatarProps>(UserAvatar);
